// SPDX-License-Identifier: AGPL-3.0-or-later

import {formatGamePlayDuration} from '@app/features/game_activity/GameActivity';
import {GameActivityIcon} from '@app/features/game_activity/GameActivityIcon';
import GameActivityStore from '@app/features/game_activity/GameActivityStore';
import {useGameActivityPresence} from '@app/features/game_activity/hooks/useGameActivityPresence';
import Authentication from '@app/features/auth/state/Authentication';
import styles from '@app/features/user/components/profile/GameActivityProfileSection.module.css';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {GameControllerIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useState} from 'react';

const PLAYING_DESCRIPTOR = msg({
	message: 'Playing',
	comment: 'Section title on a user profile when the user is playing a Steam game.',
});

interface GameActivityProfileSectionProps {
	userId: string;
}

export const GameActivityProfileSection: React.FC<GameActivityProfileSectionProps> = observer(({userId}) => {
	const {i18n} = useLingui();
	const isCurrentUser = userId === Authentication.currentUserId;
	const remoteActivity = useGameActivityPresence(userId);
	const activity = isCurrentUser ? GameActivityStore.getPresenceActivity() : remoteActivity;
	const [now, setNow] = useState(() => Date.now());

	useEffect(() => {
		if (!activity?.startedAtMs) {
			return;
		}
		const timer = window.setInterval(() => {
			setNow(Date.now());
		}, 1000);
		return () => window.clearInterval(timer);
	}, [activity?.startedAtMs]);

	if (!activity?.name) {
		return null;
	}

	const durationLabel = activity.startedAtMs ? formatGamePlayDuration(now - activity.startedAtMs) : '';

	return (
		<div className={styles.section} data-flx="user.game-activity-profile-section">
			<div className={styles.sectionTitle}>{i18n._(PLAYING_DESCRIPTOR)}</div>
			<div className={styles.card}>
				<div className={styles.gameIconPlaceholder} aria-hidden onContextMenu={(event) => event.preventDefault()}>
					<GameActivityIcon
						gameName={activity.name}
						steamAppId={activity.steamAppId}
						steamIconHash={activity.steamIconHash}
						iconDataUrl={activity.iconDataUrl}
						size={40}
						imageClassName={styles.gameIconImage}
						fallbackClassName={styles.gameIcon}
						preventContextMenu
					/>
				</div>
				<div className={styles.cardBody}>
					<div className={styles.gameName}>{activity.name}</div>
					{durationLabel ? (
						<div className={styles.durationRow}>
							<GameControllerIcon weight="fill" className={styles.durationIcon} aria-hidden />
							<span className={styles.durationText}>{durationLabel}</span>
						</div>
					) : null}
				</div>
			</div>
		</div>
	);
});
